import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import api from '../services/api';
import { toast } from 'react-hot-toast';

// Modal stillerini tanımla
const customModalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#151515',
        borderRadius: '1rem',
        padding: '2rem',
        border: '1px solid #FF3366',
        maxWidth: '400px',
        width: '90%',
        zIndex: 9999
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        zIndex: 9998
    }
};

// Modal'ı uygulamamıza bağla
Modal.setAppElement('#root');

const Posts = () => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [postToDelete, setPostToDelete] = useState(null);

    useEffect(() => {
        fetchPosts();
    }, [filter]); // Keep filter dependency for proper filtering

    const fetchPosts = async () => {
        try {
            const response = await api.get(`/posts${filter !== 'all' ? `?status=${filter}` : ''}`);
            setPosts(response.data);
        } catch (error) {
            console.error('Error fetching posts:', error);
            toast.error('Failed to fetch posts');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (post) => {
        setPostToDelete(post);
        setModalIsOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await api.delete(`/posts/${postToDelete.id}`);
            toast.success('Post deleted successfully');
            fetchPosts();
            setModalIsOpen(false);
            setPostToDelete(null);
        } catch (error) {
            console.error('Error deleting post:', error);
            toast.error(error.response?.data?.error || 'Failed to delete post');
        }
    };

    const handleDeleteCancel = () => {
        setModalIsOpen(false);
        setPostToDelete(null);
    };

    const handlePublish = async (id) => {
        try {
            await api.put(`/posts/${id}/publish`);
            toast.success('Post published successfully');
            fetchPosts();
        } catch (error) {
            console.error('Error publishing post:', error);
            toast.error('Failed to publish post');
        }
    };

    const handleEdit = (post) => {
        navigate(`/post/edit/${post.id}`);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-pulse text-[#FF3366]">Loading posts...</div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] mb-4">
                        Blog Posts
                    </h2>
                    <Link
                        to="/post/new"
                        className="px-6 py-3 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-xl hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-all duration-200 font-medium"
                    >
                        Create New Post
                    </Link>
                </div>

                {/* Filters */}
                <div className="mt-6 flex gap-4">
                    {['All', 'Published', 'Draft'].map((status) => (
                        <button
                            key={status}
                            onClick={() => setFilter(status.toLowerCase())}
                            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                                filter === status.toLowerCase()
                                    ? 'bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white'
                                    : 'text-gray-400 hover:text-[#FF3366] hover:bg-[#151515] border border-transparent hover:border-[#FF3366]/10'
                            }`}
                        >
                            {status}
                        </button>
                    ))}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={modalIsOpen}
                onRequestClose={handleDeleteCancel}
                style={customModalStyles}
                contentLabel="Delete Confirmation"
            >
                <div className="text-center">
                    <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] mb-4">Confirm Deletion</h3>
                    <p className="text-gray-300 mb-6">
                        Are you sure you want to delete "{postToDelete?.title}"? This action cannot be undone.
                    </p>
                    <div className="flex justify-center gap-4">
                        <button
                            onClick={handleDeleteCancel}
                            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#0A0A0A] text-white border border-[#FF3366]/20 hover:bg-[#151515]"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDeleteConfirm}
                            className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90"
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                    <div key={post.id} className="bg-[#151515] rounded-xl overflow-hidden shadow-lg border border-[#FF3366]/10 hover:border-[#FF3366]/30 transition-colors">
                        {/* Post Resmi */}
                        {post.featured_image && (
                            <div className="relative w-full h-48">
                                <img
                                    src={post.featured_image.startsWith('http') 
                                        ? post.featured_image 
                                        : `${process.env.REACT_APP_API_URL.replace('/api', '')}${post.featured_image}`}
                                    alt={post.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        console.error(`Failed to load image: ${post.featured_image}`);
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}

                        {/* Post İçeriği */}
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-white mb-2">
                                {post.title}
                            </h3>
                            <div className="text-gray-400 text-sm mb-4 line-clamp-3">
                                {post.excerpt || (post.content && post.content.replace(/<[^>]*>/g, ''))}
                            </div>

                            {/* Kategoriler */}
                            {post.categories && post.categories.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {post.categories.map((category, index) => (
                                        <span
                                            key={index}
                                            className="px-2 py-1 text-xs rounded-full bg-[#0A0A0A] text-[#FF3366] border border-[#FF3366]/20"
                                        >
                                            {category}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Durum Etiketi */}
                            <div className="flex items-center justify-between mb-4">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    post.status === 'published'
                                        ? 'bg-[#FF3366]/10 text-[#FF3366]'
                                        : 'bg-[#FF6B4D]/10 text-[#FF6B4D]'
                                }`}>
                                    {post.status}
                                </span>
                                <span className="text-gray-400 text-xs">
                                    {new Date(post.created_at).toLocaleDateString()}
                                </span>
                            </div>

                            {/* Aksiyon Butonları */}
                            <div className="flex justify-end gap-4">
                                <button
                                    onClick={() => handleEdit(post)}
                                    className="px-4 py-2 rounded-lg text-sm font-medium bg-[#0A0A0A] text-[#FF3366] hover:bg-[#FF3366]/10 border border-[#FF3366]/20"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDeleteClick(post)}
                                    className="px-4 py-2 rounded-lg text-sm font-medium bg-[#0A0A0A] text-white hover:bg-red-500/10 hover:text-red-400 border border-transparent hover:border-red-500/20"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Posts; 